import {
  customAliases,
  customLocales,
  customRtlLanguages,
  customTables,
} from './custom-locales/generated'

const english = {
  chat: 'Chat',
  statistics: 'Statistics',
  aiPersonality: 'AI personality',
  newConversation: 'New conversation',
  darkMode: 'Dark mode',
  welcome: 'Welcome to the assistant!',
  startConversation: 'Ask a question to start a conversation.',
  suggestions: 'Suggestions:',
  chatPlaceholder: 'What do you need help with?',
  matches: '{count} matches',
  keyFactsFrom: 'Key facts from',
  from: 'From',
  assistantThinking: 'The assistant is thinking...',
  copied: 'Copied!',
  copyAnswer: 'Copy answer',
  giveFeedback: 'Give feedback',
  feedbackTitle: 'Give feedback',
  feedbackQuestion: 'How satisfied were you with the answer?',
  comment: 'Comment',
  commentPlaceholder: 'Write a comment or choose from the list below :)',
  commentSuggestions: 'Comment suggestions:',
  feedbackConsent:
    'By sending, you consent to Semantic Lab reading your feedback and the log from this conversation.',
  cancel: 'Cancel',
  consentAndSend: 'Consent and send',
  feedbacks: 'Feedback',
  selectFeedback: 'Select feedback',
  custom: 'Custom',
  setSystemPrompt: 'Set system prompt',
  choice: 'Choice:',
  systemPromptPlaceholder: 'Enter the system prompt here...',
  save: 'Save',
  signIn: 'Sign in',
  signOutConfirm: 'Are you sure you want to sign out?',
  createStatistics: 'Create statistics',
  fillingForm: 'Filling form...',
  fillForm: 'Fill form',
  clearForm: 'Clear form',
  explanationHeading: 'How did you reach this result?',
  requiredFields: 'Please fill in all required fields',
  submitting: 'Submitting...',
  submitAnswers: 'Submit answers',
  response: 'Response:',
  noSelection: 'No selection',
  pleaseSpecify: 'Please specify...',
  quickShorter: 'Make the text shorter',
  quickSimpler: 'Make the text easier to read',
  mattilsynetSubheader: 'Securing the future for people, animals and nature',
  helsebiblioteketSubheader: 'Sandbox for Conteir and Helsebiblioteket',
  snowchatSubheader: 'Talk SNOMED here!',
  feedbackWrongTopic: 'Answered something other than the question',
  feedbackWrongAudience: 'Answer was not adapted to the target audience',
  feedbackIrrelevantLinks: 'Links to irrelevant content',
  feedbackBrokenLinks: 'Links do not work',
  feedbackTooLong: 'Answer is too long',
  feedbackGeneric: 'Generic answer',
  feedbackVeryGood: 'Very good',
  feedbackUnclear:
    'For unclear questions, it is unclear what the assistant is answering',
  personalityDoctor: 'Doctor',
  personalityPatientPublic: 'Patient / general public',
  personalitySimple: 'Simple',
  personalityPatientMedicationInfo: 'Patient medication information',
  personalityDetailedPrecise: 'Detailed and precise',
  personalityEmpatheticGem: 'Empathetic and GEM',
  personalityUser: 'User',
  personalityRelative: 'Relative / family member',
  personalityDiabetic: 'Person with diabetes',
  personalityHealthcareProfessional: 'Healthcare professional',
  personalityCarePlanExpert: 'Care plan expert',
  personalityTest: 'Test',
  personalityChat: 'Chat',
  personalityStrictChat: 'Strict chat',
  personalityClinical: 'Clinical',
  personalityDiagnosis: 'Diagnosis',
} as const

export type TranslationKey = keyof typeof english
export type TranslationTable = Record<TranslationKey, string>
type BuiltInLanguage = 'en' | 'no' | 'et' | 'zh' | 'fa'

const norwegian: TranslationTable = {
  chat: 'Samtale',
  statistics: 'Statistikk',
  aiPersonality: 'AI-personlighet',
  newConversation: 'Ny samtale',
  darkMode: 'Mørk modus',
  welcome: 'Velkommen til assistenten!',
  startConversation: 'Still et spørsmål for å starte en samtale.',
  suggestions: 'Forslag:',
  chatPlaceholder: 'Hva trenger du hjelp til?',
  matches: '{count} treff',
  keyFactsFrom: 'Nøkkelfakta fra',
  from: 'Fra',
  assistantThinking: 'Assistenten tenker...',
  copied: 'Kopiert!',
  copyAnswer: 'Kopier svar',
  giveFeedback: 'Gi tilbakemelding',
  feedbackTitle: 'Gi tilbakemelding',
  feedbackQuestion: 'Hvor fornøyd var du med svaret?',
  comment: 'Kommentar',
  commentPlaceholder: 'Skriv eller trykk i listen under :)',
  commentSuggestions: 'Kommentarforslag:',
  feedbackConsent:
    'Ved å sende samtykker du til at Semantic Lab kan lese din tilbakemelding og se loggen fra denne samtalen.',
  cancel: 'Avbryt',
  consentAndSend: 'Samtykk og send',
  feedbacks: 'Tilbakemeldinger',
  selectFeedback: 'Velg en tilbakemelding',
  custom: 'Egendefinert',
  setSystemPrompt: 'Sett systemprompt',
  choice: 'Valg:',
  systemPromptPlaceholder: 'Skriv inn systemprompt her...',
  save: 'Lagre',
  signIn: 'Logg inn',
  signOutConfirm: 'Er du sikker på at du vil logge ut?',
  createStatistics: 'Lag statistikk',
  fillingForm: 'Fyller skjema...',
  fillForm: 'Fyll skjema',
  clearForm: 'Tøm skjema',
  explanationHeading: 'Hvordan kom du frem til dette resultatet?',
  requiredFields: 'Vennligst fyll ut alle obligatoriske felt',
  submitting: 'Sender inn...',
  submitAnswers: 'Send inn svar',
  response: 'Svar:',
  noSelection: 'Ingen valg',
  pleaseSpecify: 'Vennligst spesifiser...',
  quickShorter: 'Lag teksten kortere',
  quickSimpler: 'Lag teksten mer lettlest',
  mattilsynetSubheader: 'Trygge framtiden for mennesker, dyr og natur',
  helsebiblioteketSubheader: 'Sandkasse for Conteir og Helsebiblioteket',
  snowchatSubheader: 'Snakk SNOMED her!',
  feedbackWrongTopic: 'Svarte på andre ting enn spørsmålet',
  feedbackWrongAudience: 'Svar ikke tilpasset målgruppen',
  feedbackIrrelevantLinks: 'Lenker videre til irrelevant innhold',
  feedbackBrokenLinks: 'Lenker virker ikke',
  feedbackTooLong: 'For langt svar',
  feedbackGeneric: 'Generisk svar',
  feedbackVeryGood: 'Veldig bra',
  feedbackUnclear:
    'Ved uklare spørsmål er det uklart hva assistenten faktisk svarer på',
  personalityDoctor: 'Lege',
  personalityPatientPublic: 'Pasient/befolkning',
  personalitySimple: 'Enkel',
  personalityPatientMedicationInfo: 'Pasientrettet legemiddelinformasjon',
  personalityDetailedPrecise: 'Detaljert og punktlig',
  personalityEmpatheticGem: 'Empatisk og GEM',
  personalityUser: 'Bruker',
  personalityRelative: 'Pårørende',
  personalityDiabetic: 'Diabetiker',
  personalityHealthcareProfessional: 'Helsepersonell',
  personalityCarePlanExpert: 'Pleieplanekspert',
  personalityTest: 'Test',
  personalityChat: 'Chat',
  personalityStrictChat: 'Streng chat',
  personalityClinical: 'Klinisk',
  personalityDiagnosis: 'Diagnose',
}

const estonian: TranslationTable = {
  chat: 'Vestlus',
  statistics: 'Statistika',
  aiPersonality: 'AI isiksus',
  newConversation: 'Uus vestlus',
  darkMode: 'Tume režiim',
  welcome: 'Tere tulemast assistenti!',
  startConversation: 'Vestluse alustamiseks esitage küsimus.',
  suggestions: 'Soovitused:',
  chatPlaceholder: 'Millega vajate abi?',
  matches: '{count} vastet',
  keyFactsFrom: 'Olulised faktid allikast',
  from: 'Allikast',
  assistantThinking: 'Assistent mõtleb...',
  copied: 'Kopeeritud!',
  copyAnswer: 'Kopeeri vastus',
  giveFeedback: 'Anna tagasisidet',
  feedbackTitle: 'Anna tagasisidet',
  feedbackQuestion: 'Kui rahul olite vastusega?',
  comment: 'Kommentaar',
  commentPlaceholder: 'Kirjutage kommentaar või valige allolevast loendist :)',
  commentSuggestions: 'Kommentaarisoovitused:',
  feedbackConsent:
    'Saatmisega nõustute, et Semantic Lab loeb teie tagasisidet ja selle vestluse logi.',
  cancel: 'Tühista',
  consentAndSend: 'Nõustu ja saada',
  feedbacks: 'Tagasiside',
  selectFeedback: 'Valige tagasiside',
  custom: 'Kohandatud',
  setSystemPrompt: 'Määra süsteemiviip',
  choice: 'Valik:',
  systemPromptPlaceholder: 'Sisestage süsteemiviip siia...',
  save: 'Salvesta',
  signIn: 'Logi sisse',
  signOutConfirm: 'Kas soovite kindlasti välja logida?',
  createStatistics: 'Loo statistika',
  fillingForm: 'Vormi täitmine...',
  fillForm: 'Täida vorm',
  clearForm: 'Tühjenda vorm',
  explanationHeading: 'Kuidas selle tulemuseni jõuti?',
  requiredFields: 'Palun täitke kõik kohustuslikud väljad',
  submitting: 'Saatmine...',
  submitAnswers: 'Saada vastused',
  response: 'Vastus:',
  noSelection: 'Valik puudub',
  pleaseSpecify: 'Palun täpsustage...',
  quickShorter: 'Tee tekst lühemaks',
  quickSimpler: 'Tee tekst lihtsamini loetavaks',
  mattilsynetSubheader: 'Kindlustame inimeste, loomade ja looduse tuleviku',
  helsebiblioteketSubheader: 'Conteiri ja Helsebiblioteketi liivakast',
  snowchatSubheader: 'Räägi siin SNOMEDist!',
  feedbackWrongTopic: 'Vastus käsitles midagi muud kui küsimust',
  feedbackWrongAudience: 'Vastus ei olnud sihtrühmale kohandatud',
  feedbackIrrelevantLinks: 'Lingid viivad ebaolulise sisuni',
  feedbackBrokenLinks: 'Lingid ei tööta',
  feedbackTooLong: 'Vastus on liiga pikk',
  feedbackGeneric: 'Üldine vastus',
  feedbackVeryGood: 'Väga hea',
  feedbackUnclear: 'Ebaselge küsimuse puhul on vastus samuti ebaselge',
  personalityDoctor: 'Arst',
  personalityPatientPublic: 'Patsient / elanikkond',
  personalitySimple: 'Lihtne',
  personalityPatientMedicationInfo: 'Patsiendile suunatud ravimiteave',
  personalityDetailedPrecise: 'Üksikasjalik ja täpne',
  personalityEmpatheticGem: 'Empaatiline ja GEM',
  personalityUser: 'Kasutaja',
  personalityRelative: 'Lähedane',
  personalityDiabetic: 'Diabeetik',
  personalityHealthcareProfessional: 'Tervishoiutöötaja',
  personalityCarePlanExpert: 'Hooldusplaani ekspert',
  personalityTest: 'Test',
  personalityChat: 'Vestlus',
  personalityStrictChat: 'Range vestlus',
  personalityClinical: 'Kliiniline',
  personalityDiagnosis: 'Diagnoos',
}

const chinese: TranslationTable = {
  chat: '对话',
  statistics: '统计',
  aiPersonality: 'AI 个性',
  newConversation: '新对话',
  darkMode: '深色模式',
  welcome: '欢迎使用智能助手！',
  startConversation: '请提出一个问题以开始对话。',
  suggestions: '建议：',
  chatPlaceholder: '您需要什么帮助？',
  matches: '{count} 个结果',
  keyFactsFrom: '关键事实，来源',
  from: '来源',
  assistantThinking: '助手正在思考...',
  copied: '已复制！',
  copyAnswer: '复制回答',
  giveFeedback: '提供反馈',
  feedbackTitle: '提供反馈',
  feedbackQuestion: '您对回答满意吗？',
  comment: '评论',
  commentPlaceholder: '输入评论或从下面的列表中选择 :)',
  commentSuggestions: '评论建议：',
  feedbackConsent: '发送即表示您同意 Semantic Lab 阅读您的反馈及本次对话记录。',
  cancel: '取消',
  consentAndSend: '同意并发送',
  feedbacks: '反馈',
  selectFeedback: '选择反馈',
  custom: '自定义',
  setSystemPrompt: '设置系统提示词',
  choice: '选择：',
  systemPromptPlaceholder: '在此输入系统提示词...',
  save: '保存',
  signIn: '登录',
  signOutConfirm: '您确定要退出登录吗？',
  createStatistics: '创建统计',
  fillingForm: '正在填写表单...',
  fillForm: '填写表单',
  clearForm: '清空表单',
  explanationHeading: '此结果是如何得出的？',
  requiredFields: '请填写所有必填字段',
  submitting: '正在提交...',
  submitAnswers: '提交回答',
  response: '响应：',
  noSelection: '未选择',
  pleaseSpecify: '请说明...',
  quickShorter: '缩短文本',
  quickSimpler: '使文本更易读',
  mattilsynetSubheader: '保障人类、动物和自然的未来',
  helsebiblioteketSubheader: 'Conteir 与 Helsebiblioteket 的沙盒',
  snowchatSubheader: '在这里交流 SNOMED！',
  feedbackWrongTopic: '回答了与问题无关的内容',
  feedbackWrongAudience: '回答不适合目标受众',
  feedbackIrrelevantLinks: '链接指向无关内容',
  feedbackBrokenLinks: '链接无法打开',
  feedbackTooLong: '回答太长',
  feedbackGeneric: '回答过于笼统',
  feedbackVeryGood: '非常好',
  feedbackUnclear: '问题不明确时，回答的内容也不清楚',
  personalityDoctor: '医生',
  personalityPatientPublic: '患者/公众',
  personalitySimple: '简洁',
  personalityPatientMedicationInfo: '患者用药信息',
  personalityDetailedPrecise: '详细且准确',
  personalityEmpatheticGem: '富有同理心并遵循 GEM',
  personalityUser: '用户',
  personalityRelative: '家属',
  personalityDiabetic: '糖尿病患者',
  personalityHealthcareProfessional: '医疗专业人员',
  personalityCarePlanExpert: '护理计划专家',
  personalityTest: '测试',
  personalityChat: '对话',
  personalityStrictChat: '严格对话',
  personalityClinical: '临床',
  personalityDiagnosis: '诊断',
}

const persian: TranslationTable = {
  chat: 'گفت‌وگو',
  statistics: 'آمار',
  aiPersonality: 'شخصیت هوش مصنوعی',
  newConversation: 'گفت‌وگوی جدید',
  darkMode: 'حالت تیره',
  welcome: 'به دستیار خوش آمدید!',
  startConversation: 'برای شروع گفت‌وگو یک سؤال بپرسید.',
  suggestions: 'پیشنهادها:',
  chatPlaceholder: 'در چه موردی کمک می‌خواهید؟',
  matches: '{count} نتیجه',
  keyFactsFrom: 'نکات کلیدی از',
  from: 'از',
  assistantThinking: 'دستیار در حال فکر کردن است...',
  copied: 'کپی شد!',
  copyAnswer: 'کپی پاسخ',
  giveFeedback: 'ارسال بازخورد',
  feedbackTitle: 'ارسال بازخورد',
  feedbackQuestion: 'چقدر از پاسخ راضی بودید؟',
  comment: 'نظر',
  commentPlaceholder: 'نظر خود را بنویسید یا از فهرست زیر انتخاب کنید :)',
  commentSuggestions: 'پیشنهادهای نظر:',
  feedbackConsent:
    'با ارسال، موافقت می‌کنید Semantic Lab بازخورد و سابقه این گفت‌وگو را بخواند.',
  cancel: 'لغو',
  consentAndSend: 'موافقت و ارسال',
  feedbacks: 'بازخوردها',
  selectFeedback: 'انتخاب بازخورد',
  custom: 'سفارشی',
  setSystemPrompt: 'تنظیم پیام سیستمی',
  choice: 'انتخاب:',
  systemPromptPlaceholder: 'پیام سیستمی را اینجا وارد کنید...',
  save: 'ذخیره',
  signIn: 'ورود',
  signOutConfirm: 'آیا مطمئن هستید که می‌خواهید خارج شوید؟',
  createStatistics: 'ایجاد آمار',
  fillingForm: 'در حال تکمیل فرم...',
  fillForm: 'تکمیل فرم',
  clearForm: 'پاک کردن فرم',
  explanationHeading: 'این نتیجه چگونه به دست آمد؟',
  requiredFields: 'لطفاً همه فیلدهای الزامی را تکمیل کنید',
  submitting: 'در حال ارسال...',
  submitAnswers: 'ارسال پاسخ‌ها',
  response: 'پاسخ:',
  noSelection: 'بدون انتخاب',
  pleaseSpecify: 'لطفاً مشخص کنید...',
  quickShorter: 'متن را کوتاه‌تر کن',
  quickSimpler: 'متن را خواناتر کن',
  mattilsynetSubheader: 'تضمین آینده انسان‌ها، حیوانات و طبیعت',
  helsebiblioteketSubheader: 'محیط آزمایشی Conteir و Helsebiblioteket',
  snowchatSubheader: 'اینجا درباره SNOMED گفت‌وگو کنید!',
  feedbackWrongTopic: 'پاسخ درباره موضوعی غیر از سؤال بود',
  feedbackWrongAudience: 'پاسخ متناسب با مخاطب هدف نبود',
  feedbackIrrelevantLinks: 'پیوندها به محتوای نامرتبط می‌روند',
  feedbackBrokenLinks: 'پیوندها کار نمی‌کنند',
  feedbackTooLong: 'پاسخ بیش از حد طولانی است',
  feedbackGeneric: 'پاسخ کلی',
  feedbackVeryGood: 'بسیار خوب',
  feedbackUnclear: 'برای سؤال نامشخص، پاسخ نیز روشن نیست',
  personalityDoctor: 'پزشک',
  personalityPatientPublic: 'بیمار / عموم مردم',
  personalitySimple: 'ساده',
  personalityPatientMedicationInfo: 'اطلاعات دارویی برای بیمار',
  personalityDetailedPrecise: 'دقیق و جزئی',
  personalityEmpatheticGem: 'همدلانه و GEM',
  personalityUser: 'کاربر',
  personalityRelative: 'عضو خانواده / نزدیکان',
  personalityDiabetic: 'فرد مبتلا به دیابت',
  personalityHealthcareProfessional: 'متخصص سلامت',
  personalityCarePlanExpert: 'کارشناس برنامه مراقبتی',
  personalityTest: 'آزمایش',
  personalityChat: 'گفت‌وگو',
  personalityStrictChat: 'گفت‌وگوی سخت‌گیرانه',
  personalityClinical: 'بالینی',
  personalityDiagnosis: 'تشخیص',
}

const builtInAliases: Record<string, BuiltInLanguage> = {
  en: 'en',
  english: 'en',
  no: 'no',
  nb: 'no',
  norwegian: 'no',
  norsk: 'no',
  et: 'et',
  estonian: 'et',
  eesti: 'et',
  zh: 'zh',
  chinese: 'zh',
  mandarin: 'zh',
  fa: 'fa',
  persian: 'fa',
  farsi: 'fa',
}

const aliases: Record<string, string> = {
  ...builtInAliases,
  ...customAliases,
}

const tables: Record<string, TranslationTable> = {
  en: english,
  no: norwegian,
  et: estonian,
  zh: chinese,
  fa: persian,
  ...customTables,
}

const configuredLanguage = (process.env.REACT_APP_LANGUAGE || 'norwegian')
  .trim()
  .toLowerCase()

export const frontendLanguage = aliases[configuredLanguage] || 'no'

const locales: Record<string, string> = {
  en: 'en-US',
  no: 'nb-NO',
  et: 'et-EE',
  zh: 'zh-CN',
  fa: 'fa-IR',
  ...customLocales,
}

export const frontendLocale = locales[frontendLanguage] || 'nb-NO'

export const frontendDirection =
  frontendLanguage === 'fa' || customRtlLanguages.includes(frontendLanguage)
    ? 'rtl'
    : 'ltr'

export const t = (
  key: TranslationKey,
  variables: Record<string, string | number> = {},
): string => {
  let text: string = tables[frontendLanguage][key] || english[key]
  Object.entries(variables).forEach(([name, value]) => {
    text = text.replace(`{${name}}`, String(value))
  })
  return text
}

const personalityTranslationKeys: Record<string, TranslationKey> = {
  Lege: 'personalityDoctor',
  'Pasient/befolkning': 'personalityPatientPublic',
  Enkel: 'personalitySimple',
  'Pasientrettet legemiddelinformasjon': 'personalityPatientMedicationInfo',
  'Detaljert og punktlig': 'personalityDetailedPrecise',
  'Empatisk og gem': 'personalityEmpatheticGem',
  Bruker: 'personalityUser',
  Pårørende: 'personalityRelative',
  Diabetiker: 'personalityDiabetic',
  Helsepersonell: 'personalityHealthcareProfessional',
  Pleieplaneksperten: 'personalityCarePlanExpert',
  Test: 'personalityTest',
  Chat: 'personalityChat',
  'Chat-streng': 'personalityStrictChat',
  Klinisk: 'personalityClinical',
  Diagnos: 'personalityDiagnosis',
}

export const translatePersonalityName = (name: string): string => {
  const key = personalityTranslationKeys[name]
  return key ? t(key) : name
}
